// src/hooks/creator-use-auth.js - Enhanced with better refresh handling
import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  getAuthInfo,
  getAuthToken,
  setAuthToken,
  logoutAndClear,
  initCreatorAuthStorage,
  hasCompleteAuth,
  diagnoseAuthIssues,
} from "../API/creatorTokenUtil";
import { clearGoogleSession } from '../Utils/googleSession.ts';

const CreatorAuthContext = createContext();

export const CreatorAuthProvider = ({ children }) => {
  const initializationRef = useRef(false);
  const [authToken, setAuthTokenState] = useState(null);
  const [authInfo, setAuthInfoState] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState(null);

  console.log('🔍 [CreatorAuth] Provider render:', {
    hasToken: !!authToken,
    hasInfo: !!authInfo,
    hasUsername: !!authInfo?.creator_user?.username,
    isInitialized,
    hasError: !!initError
  });

  useEffect(() => {
    const initialize = async () => {
      // Prevent double initialization
      if (initializationRef.current) {
        return;
      }
      initializationRef.current = true;

      try {
        
        // Initialize storage and get status
        const initResult = await initCreatorAuthStorage();
        
        const token = getAuthToken();
        const info = getAuthInfo();
        
        console.log('🔍 [CreatorAuth] After storage init:', {
          hasToken: !!token,
          tokenLength: token?.length || 0,
          hasInfo: !!info,
          hasUsername: !!info?.creator_user?.username,
          username: info?.creator_user?.username || 'N/A',
          initStatus: initResult?.status
        });
        
        // Update state
        setAuthTokenState(token);
        setAuthInfoState(info);
        
        // Handle incomplete auth scenarios
        if (initResult?.status === 'incomplete') {
          console.warn('⚠️ [CreatorAuth] Incomplete auth detected - token without user info');
          setInitError('incomplete_auth');
        } else if (initResult?.status === 'error') {
          console.error('❌ [CreatorAuth] Storage initialization error');
          setInitError('storage_error');
        } else if (token && info && !info.creator_user?.username) {
          console.warn('⚠️ [CreatorAuth] User info exists but username missing');
          setInitError('missing_username');
        } else {
          setInitError(null);
        }
        
      } catch (error) {
        setInitError('initialization_failed');
        
        // Run diagnostics on error
        const diagnostics = diagnoseAuthIssues();
        
      } finally {
        setIsInitialized(true);
      }
    };

    initialize();
  }, []); // Only run once on mount

  // Enhanced login that validates data before storing
  const login = useCallback((tokenObj) => {
    console.log('🔍 [CreatorAuth] Login called with:', {
      isString: typeof tokenObj === 'string',
      hasToken: tokenObj?.token ? 'YES' : 'NO',
      hasCreatorUser: tokenObj?.creator_user ? 'YES' : 'NO',
      username: tokenObj?.creator_user?.username || 'N/A'
    });
    
    try {
      // Validate input
      const token = typeof tokenObj === 'string' ? tokenObj : tokenObj.token;
      const info = typeof tokenObj === 'string' ? null : tokenObj;
      
      if (!token) {
        throw new Error('No token provided');
      }
      
      if (info && !info.creator_user?.username) {
        console.warn('⚠️ [CreatorAuth] Login data missing username');
      }
      
      // Store in persistent storage
      setAuthToken(tokenObj);
      
      // Update state immediately
      setAuthTokenState(token);
      setAuthInfoState(info);
      setInitError(null);
      
      console.log('✅ [CreatorAuth] Login successful');
      
    } catch (error) {
      console.error('❌ [CreatorAuth] Login error:', error);
      setInitError('login_failed');
    }
  }, []);

  const logout = useCallback(() => {
    console.log('🔍 [CreatorAuth] Logout called');
    try {
      clearGoogleSession();
      void logoutAndClear();
      setAuthTokenState(null);
      setAuthInfoState(null);
      setInitError(null);
      console.log('✅ [CreatorAuth] Logout successful');
    } catch (error) {
      console.error('❌ [CreatorAuth] Logout error:', error);
    }
  }, []);

  // Enhanced authorization check
  const isCreatorAuthorized = Boolean(authToken);
  const hasCompleteAuthData = hasCompleteAuth();
  
  // Detect auth issues
  const hasAuthIssue = Boolean(initError || (authToken && !authInfo?.creator_user?.username));

  const contextValue = {
    // Legacy field names that components expect
    creatorToken: authInfo,           // User profile data
    token: authToken,                 // JWT token
    isCreatorAuthorized,
    isAuthorized: isCreatorAuthorized,
    isInitialized,
    
    // Enhanced fields
    hasCompleteAuth: hasCompleteAuthData,
    authError: initError,
    hasAuthIssue,
    
    // Methods
    login,
    logout,
    unauthorize: logout,
    
    // Diagnostic method
    diagnose: diagnoseAuthIssues,
  };

  // Enhanced logging
  console.log('🔍 [CreatorAuth] Context state:', {
    hasJWTToken: !!contextValue.token,
    hasCreatorToken: !!contextValue.creatorToken,
    hasCreatorUser: !!contextValue.creatorToken?.creator_user,
    username: contextValue.creatorToken?.creator_user?.username || 'N/A',
    displayName: contextValue.creatorToken?.creator_user?.creator?.display_name || 'N/A',
    isAuthorized: contextValue.isAuthorized,
    isInitialized: contextValue.isInitialized,
    hasCompleteAuth: contextValue.hasCompleteAuth,
    authError: contextValue.authError,
    hasAuthIssue: contextValue.hasAuthIssue
  });

  return (
    <CreatorAuthContext.Provider value={contextValue}>
      {children}
    </CreatorAuthContext.Provider>
  );
};

export const useCreatorAuth = () => {
  const ctx = useContext(CreatorAuthContext);
  
  if (ctx) return ctx;
  
  // Enhanced fallback with better error handling
  try {
    const fallbackToken = getAuthToken();
    const fallbackInfo = getAuthInfo();
    const hasComplete = hasCompleteAuth();
    
    console.log('🔍 [CreatorAuth] Using fallback context:', {
      hasToken: !!fallbackToken,
      hasInfo: !!fallbackInfo,
      hasComplete,
      username: fallbackInfo?.creator_user?.username || 'N/A'
    });
    
    return {
      creatorToken: fallbackInfo,      // User profile data
      token: fallbackToken,            // JWT token  
      isCreatorAuthorized: Boolean(fallbackToken),
      isAuthorized: Boolean(fallbackToken),
      isInitialized: true,
      hasCompleteAuth: hasComplete,
      authError: fallbackToken && !hasComplete ? 'incomplete_fallback' : null,
      hasAuthIssue: Boolean(fallbackToken && !fallbackInfo?.creator_user?.username),
      
      login: () => console.warn('Creator login called outside provider'),
      logout: () => console.warn('Creator logout called outside provider'),
      unauthorize: () => console.warn('Creator unauthorize called outside provider'),
      diagnose: diagnoseAuthIssues,
    };
  } catch (error) {
    console.error('❌ [CreatorAuth] Fallback error:', error);
    
    return {
      creatorToken: null,
      token: null,
      isCreatorAuthorized: false,
      isAuthorized: false,
      isInitialized: true,
      hasCompleteAuth: false,
      authError: 'fallback_failed',
      hasAuthIssue: true,
      
      login: () => console.warn('Creator login unavailable due to error'),
      logout: () => console.warn('Creator logout unavailable due to error'),
      unauthorize: () => console.warn('Creator unauthorize unavailable due to error'),
      diagnose: () => ({ issues: ['Fallback context failed'], recommendations: ['Refresh page', 'Clear browser data'] }),
    };
  }
};